<script setup lang="ts">
/**
 * SectionPricingCards
 *
 * Conversion-focused pricing block for residential security packages.
 * Renders a labelled group of package cards with an optional variant toggle
 * (e.g. Single / Double storey), optional per-unit add-on rows, and footnotes.
 *
 * A card shows EITHER a tiered list of quantity → price rows (CCTV camera
 * counts) OR a feature list with a single headline price (alarm kit), OR a
 * "contact for pricing" state for tiers without a signed-off number.
 *
 * Published prices must be Metwide stakeholder-signed-off values. Use
 * `contactForPricing` for anything not yet confirmed.
 */
import type {
  PricingAddOn,
  PricingPackage,
  PricingVariant,
} from '~~/shared/types'

interface Props {
  title?: string
  subtitle?: string
  highlights?: { label: string; detail: string }[]
  variants?: PricingVariant[]
  packages?: PricingPackage[]
  addOnsTitle?: string
  addOnsNote?: string
  addOns?: PricingAddOn[]
  footnotes?: string[]
  ctaText?: string
  ctaHref?: string
  columns?: number
  sectionBg?: 'white' | 'neutral'
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  highlights: () => [],
  variants: () => [],
  packages: () => [],
  addOnsTitle: '',
  addOnsNote: '',
  addOns: () => [],
  footnotes: () => [],
  ctaText: 'Get a Quote',
  ctaHref: '#enquire',
  columns: 3,
  sectionBg: 'white',
})

// Desktop (lg) column count, settable per page from the content JSON. Default
// is 3; the four-package intercom pages pass 2 so the cards form a balanced
// 2x2 instead of a row of three plus a lonely fourth. Mapped to static class
// strings so Tailwind keeps them in the bundle.
const lgColumnsClass = computed(() => {
  switch (props.columns) {
    case 1:
      return 'lg:grid-cols-1'
    case 2:
      return 'lg:grid-cols-2'
    case 4:
      return 'lg:grid-cols-4'
    default:
      return 'lg:grid-cols-3'
  }
})

const hasVariants = computed(() => props.variants.length > 0)

const activeVariantKey = ref(props.variants[0]?.key ?? '')

const activePackages = computed<PricingPackage[]>(() => {
  if (!hasVariants.value) return props.packages
  const active = props.variants.find((v) => v.key === activeVariantKey.value)
  return active?.packages ?? props.variants[0]?.packages ?? []
})

const { open: openIntentModal, resolveQuoteIntent, handleLink } = useIntentModal()

// Open the quote modal (same behaviour as the final-cta / navbar quote button)
// rather than navigating; ctaHref is a /contact?need=... intent link. A package
// can override the block-level ctaHref to open its own (per-tier) quote intent.
function handleCta(pkg?: PricingPackage) {
  const href = pkg?.ctaHref ?? props.ctaHref
  if (handleLink(href)) return
  if (href.startsWith('/contact') || href === '/quote') {
    openIntentModal(resolveQuoteIntent('free-quote'))
  } else {
    navigateTo(href)
  }
}
</script>

<template>
  <section
    :class="['py-16 sm:py-20', sectionBg === 'neutral' ? 'bg-neutral-50' : 'bg-white']"
  >
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div v-if="title || subtitle" class="mb-8 max-w-3xl">
        <h2
          v-if="title"
          class="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
        >
          {{ title }}
        </h2>
        <p v-if="subtitle" class="mt-3 text-lg leading-7 text-neutral-600">
          {{ subtitle }}
        </p>
      </div>

      <!-- Camera highlights -->
      <div
        v-if="highlights.length"
        :class="[
          'mb-10 rounded-2xl border border-neutral-200 p-6 sm:p-7',
          sectionBg === 'neutral' ? 'bg-white' : 'bg-neutral-50',
        ]"
      >
        <ul class="grid gap-x-6 gap-y-3 sm:grid-cols-3">
          <li
            v-for="(h, i) in highlights"
            :key="i"
            class="flex items-start gap-2 text-sm text-neutral-600"
          >
            <Icon name="lucide:check" size="16" class="mt-0.5 shrink-0 text-primary-500" />
            <span><span class="font-semibold text-slate-900">{{ h.label }}</span> {{ h.detail }}</span>
          </li>
        </ul>
      </div>

      <!-- Variant toggle (e.g. Single / Double storey) -->
      <div
        v-if="hasVariants"
        class="mb-8 inline-flex rounded-full border border-neutral-200 bg-white p-1"
        role="tablist"
        :aria-label="title || 'Pricing options'"
      >
        <button
          v-for="variant in variants"
          :key="variant.key"
          type="button"
          role="tab"
          :aria-selected="activeVariantKey === variant.key"
          :class="[
            'rounded-full px-5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            activeVariantKey === variant.key
              ? 'bg-primary-600 text-white'
              : 'text-slate-600 hover:text-slate-900',
          ]"
          @click="activeVariantKey = variant.key"
        >
          {{ variant.label }}
        </button>
      </div>

      <!-- Package cards -->
      <div class="grid gap-6 md:grid-cols-2" :class="lgColumnsClass">
        <div
          v-for="(pkg, index) in activePackages"
          :key="`${activeVariantKey}-${index}`"
          :class="[
            'relative flex flex-col rounded-2xl p-6 transition-colors',
            pkg.highlight
              ? 'border-2 border-primary-500 bg-primary-50/40 shadow-xl shadow-primary-900/5 lg:-mt-2 lg:mb-2'
              : 'border border-neutral-200 bg-white',
          ]"
        >
          <!-- Badge -->
          <div v-if="pkg.badge" class="absolute -top-3 right-6">
            <span
              class="rounded-full bg-mw-accent-500 px-3 py-1 text-xs font-semibold text-slate-900"
            >
              {{ pkg.badge }}
            </span>
          </div>

          <!-- Product image (preferred) or icon chip -->
          <div
            v-if="pkg.image"
            class="mb-5 flex h-40 items-center justify-center"
          >
            <img
              :src="pkg.image.src"
              :alt="pkg.image.alt"
              class="h-full w-full object-contain"
              loading="lazy"
            />
          </div>
          <div
            v-else-if="pkg.icon"
            class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-mw-accent-50 text-mw-accent-500"
          >
            <Icon :name="pkg.icon" size="24" />
          </div>

          <!-- Brand eyebrow + name -->
          <p
            v-if="pkg.brand"
            class="text-xs font-bold uppercase tracking-widest text-slate-500"
          >
            {{ pkg.brand }}
          </p>
          <h3 class="mt-1 text-xl font-extrabold text-slate-900">
            {{ pkg.name }}
          </h3>
          <p v-if="pkg.description" class="mt-2 text-sm leading-snug text-neutral-600">
            {{ pkg.description }}
          </p>

          <!-- Tiered price rows (CCTV) -->
          <ul
            v-if="pkg.rows && pkg.rows.length"
            class="mt-5 divide-y divide-neutral-100 border-y border-neutral-100"
          >
            <li
              v-for="(row, rowIndex) in pkg.rows"
              :key="rowIndex"
              class="flex items-baseline justify-between gap-4 py-2.5"
            >
              <span class="text-sm text-neutral-600">{{ row.label }}</span>
              <span class="text-base font-bold tracking-tight text-slate-900">
                {{ row.price }}
              </span>
            </li>
          </ul>

          <!-- Feature list (alarm-style) -->
          <ul v-if="pkg.features && pkg.features.length" class="mt-5 space-y-2.5">
            <li
              v-for="(feature, featureIndex) in pkg.features"
              :key="featureIndex"
              class="flex items-start gap-2 text-sm text-neutral-600"
            >
              <Icon
                name="lucide:check"
                size="16"
                class="mt-0.5 shrink-0 text-primary-500"
              />
              {{ feature }}
            </li>
          </ul>

          <!-- Spacer fills the remaining height so the footer sits at the card
               bottom and prices/CTAs line up across every card in the row. -->
          <div class="grow"></div>

          <!-- Footer: price + CTA. mt-8 keeps a clear gap above the divider
               even when the feature list fills the tallest card in the row. -->
          <div class="mt-8 border-t border-neutral-100 pt-5">
            <!-- Single headline price (alarm-style) -->
            <div
              v-if="pkg.price && !pkg.contactForPricing"
              class="flex items-baseline gap-1.5"
            >
              <span class="text-3xl font-extrabold tracking-tight text-slate-900">
                {{ pkg.price }}
              </span>
              <span v-if="pkg.priceSuffix" class="text-sm font-medium text-slate-500">
                {{ pkg.priceSuffix }}
              </span>
            </div>

            <!-- Contact-for-pricing state -->
            <p
              v-if="pkg.contactForPricing"
              class="text-base font-semibold text-slate-700"
            >
              {{ pkg.contactLabel || 'Contact us for pricing' }}
            </p>

            <!-- CTA -->
            <button
              type="button"
              :class="[
                'mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                pkg.highlight
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'border-2 border-primary-500 bg-white text-primary-600 hover:bg-primary-50',
              ]"
              @click="handleCta(pkg)"
            >
              {{ pkg.contactForPricing ? 'Contact Us' : (pkg.ctaText ?? ctaText) }}
            </button>
          </div>
        </div>
      </div>

      <!-- Add-ons -->
      <div v-if="addOns.length" class="mt-14">
        <h3
          v-if="addOnsTitle"
          class="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
        >
          {{ addOnsTitle }}
        </h3>
        <p v-if="addOnsNote" class="mt-3 max-w-3xl text-lg leading-7 text-neutral-600">
          {{ addOnsNote }}
        </p>
        <ul class="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <li
            v-for="(addOn, addOnIndex) in addOns"
            :key="addOnIndex"
            class="flex flex-col rounded-2xl border border-neutral-200 bg-white p-5 transition-colors hover:border-primary-300"
          >
            <div
              v-if="addOn.icon"
              class="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-mw-accent-50 text-mw-accent-500"
            >
              <Icon :name="addOn.icon" size="22" />
            </div>
            <p class="text-base font-bold text-slate-900">{{ addOn.name }}</p>
            <p v-if="addOn.spec" class="mt-1.5 grow text-sm leading-snug text-neutral-600">
              {{ addOn.spec }}
            </p>
            <span
              class="mt-4 inline-flex w-fit rounded-full bg-primary-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-700"
            >
              {{ addOn.price }}
            </span>
          </li>
        </ul>
      </div>

      <!-- Footnotes -->
      <div v-if="footnotes.length" class="mt-8 space-y-1">
        <p
          v-for="(note, noteIndex) in footnotes"
          :key="noteIndex"
          class="text-xs text-neutral-500"
        >
          {{ note }}
        </p>
      </div>
    </div>
  </section>
</template>
